import React, { useCallback } from 'react'
import { intl } from 'lang/intl'
import { type FormikHelpers } from 'formik'
import { FormattedMessage } from 'react-intl'

import * as TtForms from '@23/react-forms'

import { useModal } from '../../../Layout/Modal'
import { useWebinarColors } from '../../../Designs/blocs/useWebinarColors'
import { setAudienceName } from '../../blocs/useAudienceName'

import './AskAudienceNameModal.scss'

type FormValues = {
  name: string
  style?: React.CSSProperties
}

interface AskAudienceNameProps {
  onSubmit: () => void
  style?: React.CSSProperties
}

const AskAudienceName = ({ onSubmit, style }: AskAudienceNameProps) => {
  const handleSubmit = useCallback(
    async (values: FormValues, _: FormikHelpers<FormValues>) => {
      if (!values.name) return Promise.reject()

      setAudienceName(values.name)
      return onSubmit()
    },
    [onSubmit]
  )

  return (
    <>
      <FormattedMessage defaultMessage="Your name is required to interact with the webinar and its participants." />
      <TtForms.Form<FormValues>
        style={style}
        className="audience-name-form"
        label={intl.formatMessage({
          defaultMessage: 'Set your name.',
        })}
        initialValues={{
          name: '',
        }}
        enableReinitialize
        novalidate
        onSubmit={handleSubmit}
      >
        <TtForms.TextField
          className="audience-name-input"
          label={intl.formatMessage({ defaultMessage: 'Name' })}
          name="name"
          hideLabel
          placeholder={intl.formatMessage({
            defaultMessage: 'Type in your name...',
          })}
          required
        />

        <TtForms.SubmitButton
          size="medium"
          className="audience-name-submit"
          label={intl.formatMessage({ defaultMessage: 'Save' })}
          pill
        >
          <FormattedMessage defaultMessage="Submit" />
        </TtForms.SubmitButton>
      </TtForms.Form>
    </>
  )
}

const useAskAudienceName = () => {
  const { openModal, closeModal } = useModal()
  const { accent } = useWebinarColors()

  return useCallback(() => {
    return new Promise<boolean>((resolve) => {
      openModal({
        className: 'ask-audience-name-modal',
        label: intl.formatMessage({
          defaultMessage: "What's your name?",
        }),
        body: (
          <AskAudienceName
            style={{
              '--webinar-color-accent': accent.color,
              '--webinar-color-accent-text': accent.highContrast.color,
            }}
            onSubmit={() => resolve(true)}
          />
        ),
        onClose: () => resolve(false),
        isAlert: false,
      })
    }).finally(closeModal)
  }, [closeModal, openModal, accent.color, accent.highContrast.color])
}

export { useAskAudienceName }
